import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowLeft, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_ATTEMPTS = 30;        // 30 * 2s = 60 seconds
const POLL_INTERVAL_MS = 2000;

const Success = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paymentIntentId = params.get("payment_intent");
  const orderId = params.get("order_id");
  const [state, setState] = useState({ status: "polling", data: null, error: "", attempts: 0 });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId && !paymentIntentId && !orderId) {
      setState({ status: "error", data: null, error: "Missing payment reference.", attempts: 0 });
      return;
    }

    let timer;
    let cancelled = false;

    const fetchStatus = async () => {
      // Prefer PaymentIntent endpoint (Elements flow), then session endpoint, then order lookup
      if (paymentIntentId) {
        const r = await axios.get(`${API}/orders/by-payment-intent/${paymentIntentId}`);
        return r.data;
      }
      if (sessionId) {
        const r = await axios.get(`${API}/orders/status/${sessionId}`);
        return r.data;
      }
      const r = await axios.get(`${API}/orders/by-payment-intent/${orderId}`).catch(() => null);
      return r?.data || null;
    };

    const poll = async () => {
      if (cancelled) return;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState((s) => ({ ...s, status: "timeout" }));
        return;
      }
      attemptsRef.current += 1;
      try {
        const data = await fetchStatus();
        const order = data?.order;
        const paymentStatus = data?.payment_status;
        const orderStatus = data?.status;

        if (paymentStatus === "paid" || order?.payment_status === "paid") {
          setState({ status: "paid", data, error: "", attempts: attemptsRef.current });
          return;
        }
        if (paymentStatus === "failed" || order?.payment_status === "failed" || order?.status === "cancelled") {
          setState({ status: "failed", data, error: "Payment failed.", attempts: attemptsRef.current });
          return;
        }
        if (orderStatus === "expired") {
          setState({ status: "expired", data, error: "", attempts: attemptsRef.current });
          return;
        }

        setState({ status: "polling", data, error: "", attempts: attemptsRef.current });
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setState({ status: "error", data: null, error: e?.response?.data?.detail || e.message, attempts: attemptsRef.current });
      }
    };
    poll();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [sessionId, paymentIntentId, orderId]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16" style={{ background: "var(--awd-bg)" }}>
      <div className="max-w-xl w-full text-center" data-testid="success-page">
        <div className="kente-stripe mb-12" />

        {state.status === "polling" && (
          <div data-testid="payment-processing">
            <Loader2 size={48} className="mx-auto animate-spin" style={{ color: "var(--awd-ochre)" }} />
            <h1 className="font-display text-4xl mt-6">Confirming your payment…</h1>
            <p className="mt-3" style={{ color: "var(--awd-text-2)" }}>
              Cash App Pay can take a few seconds to clear. Hang tight — we'll catch it.
            </p>
            <p className="mt-2 text-xs uppercase tracking-widest" style={{ color: "var(--awd-text-3)" }}>
              Attempt {state.attempts} of {MAX_ATTEMPTS}
            </p>
            <div className="mt-6 w-full max-w-xs mx-auto h-1" style={{ background: "var(--awd-border)" }}>
              <div
                className="h-full transition-[width] duration-500"
                style={{ width: `${(state.attempts / MAX_ATTEMPTS) * 100}%`, background: "var(--awd-ochre)" }}
              />
            </div>
          </div>
        )}

        {state.status === "paid" && (
          <div data-testid="payment-success">
            <CheckCircle2 size={56} className="mx-auto" style={{ color: "var(--awd-ochre)" }} />
            <h1 className="font-display text-5xl mt-6 leading-none">
              Payment <span className="italic" style={{ color: "var(--awd-ochre)" }}>confirmed</span>.
            </h1>
            <p className="mt-4 text-lg" style={{ color: "var(--awd-text-2)" }}>
              Your order is locked in. We'll text pickup details shortly.
            </p>
            {state.data?.order && (
              <div className="mt-8 p-6 text-left border inline-block" style={{ borderColor: "var(--awd-border)" }}>
                <div className="label-mini mb-3">Order Summary</div>
                {state.data.order.items?.map((it) => (
                  <div key={it.id} className="flex justify-between text-sm py-1">
                    <span>{it.name} × {it.quantity}</span>
                    <span style={{ color: "var(--awd-text-2)" }}>${(it.price * it.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between mt-3 pt-3 border-t" style={{ borderColor: "var(--awd-border)" }}>
                  <span className="label-mini">Total Paid</span>
                  <span className="font-display text-xl" style={{ color: "var(--awd-ochre)" }}>${state.data.order.amount?.toFixed(2)}</span>
                </div>
                <div className="mt-4 text-xs" style={{ color: "var(--awd-text-3)" }}>
                  Ref: {state.data.order.id?.slice(0, 8)}
                </div>
              </div>
            )}
            <div className="mt-10">
              <Link to="/" className="btn-ghost"><ArrowLeft size={16} /> Back to home</Link>
            </div>
          </div>
        )}

        {state.status === "failed" && (
          <div data-testid="payment-failed">
            <XCircle size={56} className="mx-auto" style={{ color: "var(--awd-jollof)" }} />
            <h1 className="font-display text-4xl mt-6">Payment failed</h1>
            <p className="mt-3" style={{ color: "var(--awd-text-2)" }}>
              Your payment didn't go through. No charges were made. You can try again with a different method.
            </p>
            <Link to="/" className="btn-primary mt-8 inline-flex">Try again</Link>
          </div>
        )}

        {state.status === "expired" && (
          <>
            <AlertTriangle size={48} className="mx-auto" style={{ color: "var(--awd-jollof)" }} />
            <h1 className="font-display text-4xl mt-6">Session expired</h1>
            <p className="mt-3" style={{ color: "var(--awd-text-2)" }}>Please return to the menu and try again.</p>
            <Link to="/" className="btn-primary mt-8 inline-flex">Back to menu</Link>
          </>
        )}

        {state.status === "timeout" && (
          <div data-testid="payment-timeout">
            <AlertTriangle size={48} className="mx-auto" style={{ color: "var(--awd-ochre)" }} />
            <h1 className="font-display text-4xl mt-6">Still processing</h1>
            <p className="mt-3" style={{ color: "var(--awd-text-2)" }}>
              We couldn't confirm in 60 seconds, but Cash App Pay sometimes takes longer. You'll receive a text once it's confirmed — no need to pay again.
            </p>
            <Link to="/" className="btn-ghost mt-8 inline-flex">Back to home</Link>
          </div>
        )}

        {state.status === "error" && (
          <>
            <AlertTriangle size={48} className="mx-auto" style={{ color: "var(--awd-jollof)" }} />
            <h1 className="font-display text-4xl mt-6">Something went wrong</h1>
            <p className="mt-3" style={{ color: "var(--awd-text-2)" }}>{state.error}</p>
            <Link to="/" className="btn-primary mt-8 inline-flex">Back to home</Link>
          </>
        )}
      </div>
    </div>
  );
};

export default Success;
